import { DatalayerService } from '@analytics/datalayer';
import { TrackLinkDirective, TrackStepDirective } from '@analytics/events';
import { AnalyticsActionTypes } from '@analytics/models';
import { AnalyticsUtils, ToolTrackingUtils } from '@analytics/utils';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { TypedComponentRendering } from '@core/jss-models';
import {
  GenericLinkDirective,
  GenericRichTextDirective,
  TextInterpolationPipe,
  TextInterpolationService,
} from '@core/jss-utils';
import { getEcmpLogo } from '@core/navigation-store';
import { Status } from '@essent/common';
import {
  CrntButton,
  CrntFormControl,
  CrntFormControlValidationMessage,
  CrntIcon,
  CrntTextInput,
} from '@essent/current-web-angular';
import type { InstallationAddress } from '@essent/installation-address';
import type { FullCalculateFormValues } from '@innogy/become-a-customer/shared';
import {
  type CalculateComponentContent,
  type CalculateComponentRendering,
} from '@innogy/become-a-customer/shared/interfaces';
import {
  getPromoCodeFormState,
  getPromoCodeId,
} from '@innogy/become-a-customer/state';
import type { DropdownItem } from '@innogy/common-ui/forms';
import { MediaObserverService } from '@innogy/shared/responsive-tools';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { CardComponent } from '@legacy/common-ui-partials';
import { Store } from '@ngrx/store';
import { JssModule } from '@sitecore-jss/sitecore-jss-angular';
import { FormGroupState, NgrxFormsModule } from 'ngrx-forms';
import { IsInvalidPipe } from '@innogy/utils-deprecated';

import { CalculateFormAddressComponent } from './calculate-form-address/calculate-form-address.component';
import { CalculateFormCheckboxesComponent } from './calculate-form-checkboxes/calculate-form-checkboxes.component';
import { CalculateFormExistingCustomerComponent } from './calculate-form-existing-customer/calculate-form-existing-customer.component';
import { CalculateFormMeterComponent } from './calculate-form-meter/calculate-form-meter.component';
import { CalculateFormQuestionnaireTriggerComponent } from './calculate-form-questionnaire-trigger/calculate-form-questionnaire-trigger.component';
import { CalculateFormSolarComponent } from './calculate-form-solar/calculate-form-solar.component';
import { CalculateFormStickerComponent } from './calculate-form-sticker/calculate-form-sticker.component';
import { CalculateFormUsageComponent } from './calculate-form-usage/calculate-form-usage.component';

@Component({
  selector: 'wl-calculate-form',
  templateUrl: './calculate-form.component.html',
  styleUrls: [
    './calculate-form.component.ed.scss',
    './calculate-form.component.essent.scss',
  ],
  standalone: true,
  imports: [
    CommonModule,
    JssModule,
    FormsModule,
    NgrxFormsModule,
    AutomationIdDirective,
    IsInvalidPipe,
    TrackStepDirective,
    TrackLinkDirective,
    AutomationIdDirective,
    GenericRichTextDirective,
    CrntFormControl,
    CrntButton,
    CrntFormControlValidationMessage,
    CrntTextInput,
    CalculateFormStickerComponent,
    CalculateFormAddressComponent,
    CalculateFormExistingCustomerComponent,
    CalculateFormQuestionnaireTriggerComponent,
    CalculateFormMeterComponent,
    CalculateFormUsageComponent,
    CalculateFormCheckboxesComponent,
    CardComponent,
    CalculateFormSolarComponent,
    CrntIcon,
    TextInterpolationPipe,
    GenericLinkDirective,
  ],
  providers: [TextInterpolationService],
})
export class CalculateFormComponent {
  private readonly toolTrackingUtils = inject(ToolTrackingUtils);
  private readonly analyticsUtils = inject(AnalyticsUtils);
  private readonly dataLayerService = inject(DatalayerService);
  private readonly deviceInfo = inject(MediaObserverService).deviceInfo;
  private readonly store$ = inject(Store);

  readonly rendering =
    input<TypedComponentRendering<CalculateComponentRendering>>();
  readonly content = input.required<CalculateComponentContent>();
  readonly formState =
    input.required<FormGroupState<FullCalculateFormValues>>();
  readonly installationAddressStatus = input<Status>();
  readonly installationAddresses = input<InstallationAddress[]>();
  readonly houseNumberExtensions = input<DropdownItem[]>();
  readonly showManualHouseNumberAdditionInput = input<boolean>();
  readonly submitButtonDisabled = input<boolean>();
  readonly showSpinnerWhenSubmitButtonDisabled = input<boolean>();
  readonly isInModal = input<boolean>(false);
  readonly installationAddressNotFound = input<boolean>();
  readonly useAccessibleCalculator = input<boolean>();

  readonly promoCode = this.store$.selectSignal(getPromoCodeId);
  readonly promoCodeFormState = this.store$.selectSignal(getPromoCodeFormState);

  readonly affiliateLogo = this.store$.selectSignal(getEcmpLogo);

  submitCalculate = output();
  openModal = output<boolean>();
  hideHouseNumberInput = output();
  submitAlternateAddress = output();

  readonly hideTriggerContainer = signal(false);
  readonly addressNextButtonClicked = signal(false);

  readonly controls = computed(() => this.formState().controls);

  readonly isAddressManualEntryEnabled = computed(
    () => this.content().enableAlternateAddressEntry
  );
  readonly isUsageQuestionnaireTriggerEnabled = computed(
    () => this.content()?.enableUsageQuestionnaireTrigger
  );
  readonly showPromoCodeForm = computed(
    () =>
      this.content().enablePromoCodeInput &&
      !(
        this.promoCodeFormState().isSubmitted &&
        this.promoCodeFormState().isValid
      )
  );

  /**
   * When the alternate address feature is enabled, this check will pass when the user has completed that entry
   * Otherwise it is always true
   */
  readonly isAddressManualEntryCompleted = computed(() =>
    this.isAddressManualEntryEnabled() ? this.addressNextButtonClicked() : true
  );

  /**
   * When the usage questionnaire trigger feature is enabled, this check will pass when the user has completed that step
   * Otherwise it is always true
   */
  readonly isUsageQuestionnaireTriggerCompleted = computed(() =>
    this.isUsageQuestionnaireTriggerEnabled()
      ? this.hideTriggerContainer()
      : true
  );

  /**
   * Checks if the usage form has been (partially) completed
   */
  readonly usageQuestionnaireFormCompleted = computed(() => {
    if (this.controls().doubleMeter?.value) {
      return (
        this.controls().electricityUsageNormal?.isValid ||
        this.controls().electricityUsageOffPeak?.isValid
      );
    } else {
      return this.controls().electricityUsage?.isValid;
    }
  });

  /**
   * Determines if the usage questionnaire trigger should be shown, only if:
   * - The feature is enabled
   * - It is not already dismissed
   * - The usage form has not been (partially) completed yet
   * - The manual address entry feature is enabled and completed
   */
  readonly showUsageQuestionnaireTrigger = computed(() => {
    return !(
      !this.isUsageQuestionnaireTriggerEnabled() ||
      this.hideTriggerContainer() ||
      this.usageQuestionnaireFormCompleted() ||
      !this.isAddressManualEntryCompleted()
    );
  });

  /**
   * Determines if the usage form should be shown:
   * - The usage form has been (partially) completed (also happens with the questionnaire modal)
   * - The usage trigger feature is enabled and has been dismissed
   * - The manual address feature has been enabled and it is completed
   */
  readonly showCalculateFormContainer = computed(() => {
    return (
      this.usageQuestionnaireFormCompleted() ||
      (this.isUsageQuestionnaireTriggerCompleted() &&
        this.isAddressManualEntryCompleted())
    );
  });

  /**
   * Shows an affiliate logo in the calculate card header if:
   * - A logo is configured and recognized by channel recognition, and:
   * - The calculate content has the current device type configured as an allowed type
   */
  readonly currentDevice = computed(() => {
    if (this.deviceInfo().isDesktop) {
      return 'desktop';
    } else if (this.deviceInfo().isTablet) {
      return 'tablet';
    } else if (this.deviceInfo().isMobile) {
      return 'mobile';
    } else {
      return 'unknown';
    }
  });
  readonly shownAffiliateLogo = computed(() =>
    this.affiliateLogo() &&
    this.content().affiliateLogoShownDevices.includes(this.currentDevice())
      ? this.affiliateLogo()
      : null
  );

  setAddressNextButtonClicked() {
    this.addressNextButtonClicked.set(
      !(
        this.controls()?.postalCode?.isPristine ||
        this.controls()?.postalCode?.isInvalid ||
        this.controls()?.houseNumber?.isPristine ||
        this.controls()?.houseNumber?.isInvalid
      )
    );
    this.submitAlternateAddress.emit();
  }

  async trackPromoCodeSubmit() {
    if (!this.promoCodeFormState().isValid) {
      this.dataLayerService.pushEvent(
        this.toolTrackingUtils.createToolEvent(
          AnalyticsActionTypes.LAST_STEP,
          {
            result: {
              promoCode: this.promoCodeFormState().value.promoCode,
            },
          },
          {
            name: 'calculate',
            stepname: 'promo code',
            placement: await this.analyticsUtils.getElementPlacement(
              this.rendering()?.componentName
            ),
            step: 0,
          }
        )
      );
    }
  }

  readonly submitButtonState = computed(() => {
    if (!this.submitButtonDisabled()) {
      return 'enabled';
    }

    if (this.content().useAccessibleCalculator) {
      return 'processing';
    }

    return 'disabled';
  });
}
